import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Alert, Box, Typography,
  CircularProgress, Chip, Divider, IconButton, Tooltip
} from '@mui/material';
import { Print, Refresh, CheckCircle, Cancel, Settings } from '@mui/icons-material';
import {
  getPrinterConfig,
  setPrinterConfig,
  checkQzAvailable,
  getAvailablePrinters,
  getPrintServiceStatus
} from '../services/printService';

export default function PrinterConfigDialog({ open, onClose }) {
  const [config, setConfig] = useState(getPrinterConfig());
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qzAvailable, setQzAvailable] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      checkStatus();
    }
  }, [open]);

  const checkStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const available = await checkQzAvailable();
      setQzAvailable(available);
      
      if (available) {
        const printerList = await getAvailablePrinters();
        setPrinters(printerList);
      }
    } catch (e) {
      setError('Failed to connect to QZ Tray');
      setQzAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    checkStatus();
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setPrinterConfig(config);
    onClose();
  };

  const handleCancel = () => {
    setConfig(getPrinterConfig());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings />
          Printer Configuration
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* QZ Tray Status */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              QZ Tray Status
            </Typography>
            <Tooltip title="Refresh connection">
              <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking connection...</Typography>
            </Box>
          ) : qzAvailable ? (
            <Chip 
              icon={<CheckCircle />} 
              label="QZ Tray Connected" 
              color="success" 
              size="small"
            />
          ) : (
            <Chip 
              icon={<Cancel />} 
              label="QZ Tray Not Available" 
              color="default" 
              size="small"
            />
          )}
          
          {!qzAvailable && !loading && (
            <Alert severity="info" sx={{ mt: 1 }}>
              QZ Tray is not running. Printing will use the browser print dialog instead.
              <br />
              <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer">
                Download QZ Tray
              </a>
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
          )}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Enable/Disable QZ Tray */}
        <FormControlLabel
          control={
            <Switch
              checked={config.useQzTray}
              onChange={(e) => handleChange('useQzTray', e.target.checked)}
            />
          }
          label="Use QZ Tray for silent printing (when available)"
          sx={{ mb: 2 }}
        />
        
        {/* Printer Selection */}
        {config.useQzTray && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth disabled={!qzAvailable || printers.length === 0}>
              <InputLabel>Packing Slip Printer</InputLabel>
              <Select
                value={config.packingSlipPrinter || ''}
                onChange={(e) => handleChange('packingSlipPrinter', e.target.value)}
                label="Packing Slip Printer"
              >
                <MenuItem value="">
                  <em>Use browser default</em>
                </MenuItem>
                {printers.map((printer) => (
                  <MenuItem key={printer} value={printer}>
                    {printer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth disabled={!qzAvailable || printers.length === 0}>
              <InputLabel>Label Printer (DX Labels)</InputLabel>
              <Select
                value={config.labelPrinter || ''}
                onChange={(e) => handleChange('labelPrinter', e.target.value)}
                label="Label Printer (DX Labels)"
              >
                <MenuItem value="">
                  <em>Use browser default</em>
                </MenuItem>
                {printers.map((printer) => (
                  <MenuItem key={printer} value={printer}>
                    {printer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {printers.length === 0 && qzAvailable && (
              <Alert severity="warning">
                No printers found. Make sure printers are installed and try refreshing.
              </Alert>
            )}
          </Box>
        )}
        
        {!config.useQzTray && (
          <Alert severity="info">
            Browser print dialogs will be used. You'll select the printer each time.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Small button component to open the config dialog
export function PrinterConfigButton({ size = 'small', variant = 'outlined' }) {
  const [open, setOpen] = useState(false);
  
  return (
    <>
      <Tooltip title="Printer Settings">
        <Button
          size={size}
          variant={variant}
          startIcon={<Settings />}
          onClick={() => setOpen(true)}
        >
          Printers
        </Button>
      </Tooltip>
      <PrinterConfigDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}